import type { PayoutBatchSocketPayload } from "@/modules/dashboard/notifications/services/notifications-socket.service";

import type { PayoutBatchCounters, PayoutBatchDetail, PayoutBatchStatus } from "../types/payoutBatches.types";
import { isLiveStatus } from "./batchStatus";

export interface LiveMergeResult {
  next: PayoutBatchDetail;
  /** False when the push was for another batch or older than what is already held. */
  accepted: boolean;
  lastSequence: number;
  progressPct: number | null;
}

const isNewer = (candidate: string, current: string): boolean => {
  const a = Date.parse(candidate);
  const b = Date.parse(current);
  if (Number.isNaN(a) || Number.isNaN(b)) return true;
  return a >= b;
};

/**
 * A push carries status and counters only, so the whole batch is re-read when it reached another non-live
 * status or someone else changed it since the snapshot held — never for the echo of this screen's own
 * mutation, whose answer already carried the full batch. Live statuses are polled instead.
 */
export function shouldReadAfterPush(
  held: Pick<PayoutBatchDetail, "status" | "updated_at"> | null,
  payload: Pick<PayoutBatchSocketPayload, "status" | "updated_at">,
): boolean {
  const status = payload.status as PayoutBatchStatus;
  if (!held || isLiveStatus(status)) return false;
  if (held.status !== status) return true;
  return Date.parse(payload.updated_at) > Date.parse(held.updated_at);
}

/**
 * Applies one `payout_batch:updated` push onto the batch the screen holds. Pushes are advisory and can arrive
 * out of order, so two guards decide: `sequence` must not go backwards (it grows with every processed row)
 * and `updated_at` must not be older than the last full read. Only what the push carries is merged — status,
 * counters, `updated_at` — everything else (totals, summary) waits for the next `GET /:id`, which always
 * wins.
 */
export function mergeBatchSnapshot(
  current: PayoutBatchDetail,
  payload: PayoutBatchSocketPayload,
  lastSequence: number,
): LiveMergeResult {
  const rejected: LiveMergeResult = {
    next: current,
    accepted: false,
    lastSequence,
    progressPct: null,
  };
  if (payload.id !== current.id) return rejected;
  if (payload.sequence < lastSequence) return rejected;
  if (!isNewer(payload.updated_at, current.updated_at)) return rejected;

  const counters = {
    ...current.counters,
    ...(payload.counters as Partial<PayoutBatchCounters>),
  };
  const status = payload.status as PayoutBatchStatus;
  return {
    next: {
      ...current,
      status,
      counters,
      updated_at: payload.updated_at,
      summary: { ...current.summary, counters },
    },
    accepted: true,
    lastSequence: payload.sequence,
    progressPct: payload.progress_pct,
  };
}
